import { type ReactNode } from 'react';
import { useMaintenanceFlag, clearBypass } from '@/hooks/useMaintenanceFlag';
import MaintenancePage from '@/pages/MaintenancePage';

interface MaintenanceShieldProps {
  children: ReactNode;
}

/**
 * MaintenanceShield
 *
 * Wraps its children and renders <MaintenancePage /> in their place when either
 * toggle is ON:
 *   1. Build-time env flag: VITE_MAINTENANCE_MODE="true" or "1"
 *   2. DB flag:             system_settings WHERE key = 'maintenance_mode' AND value = true
 *
 * Both toggles default to OFF — this component is inert in the current build.
 *
 * Bypass: visit /?nws=<VITE_BYPASS_SECRET> to set localStorage flag and skip the shield.
 * When bypass is active a discreet fixed badge is shown in the app corner so it can be cleared.
 */
export function MaintenanceShield({ children }: MaintenanceShieldProps) {
  const { isLoading, isOn, bypass } = useMaintenanceFlag();

  if (isLoading) return null;
  if (isOn) return <MaintenancePage />;

  return (
    <>
      {children}
      {bypass && (
        <div
          style={{
            position: 'fixed',
            bottom: '1rem',
            right: '1rem',
            zIndex: 9999,
          }}
        >
          <button
            onClick={() => { clearBypass(); window.location.reload(); }}
            style={{
              background: 'rgba(0,0,0,0.55)',
              border: 'none',
              borderRadius: '6px',
              color: '#ccc',
              cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '0.65rem',
              letterSpacing: '0.03em',
              padding: '4px 10px',
            }}
          >
            exit bypass
          </button>
        </div>
      )}
    </>
  );
}
